import { useEffect, useState } from 'react';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useT } from '@/shared/i18n';
import { C } from '@/shared/locales/checkout/keys';

// v2: wording changed to explain why payment is manual, so returning visitors see it once more.
const SEEN_KEY = 'gc.paymentNotice.v2';

const METHODS = 'Card, Wise, Crypto or E-Transfer';

function seen() {
  try {
    return window.localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function markSeen() {
  try {
    window.localStorage.setItem(SEEN_KEY, '1');
  } catch {
    // Private mode: the notice may show again next visit.
  }
}

const POINTS = [
  { icon: 'ShieldAlert', title: C.pt1, text: C.pd1 },
  { icon: 'UserCheck', title: C.pt2, text: C.pd2 },
  { icon: 'Link', title: C.pt3, text: C.pd3 },
  { icon: 'CreditCard', title: C.pt4, text: C.pd4 },
];

/** One-time pop-up per visitor explaining why payment is manual and happens off-site. */
export default function PaymentNotice() {
  const { t } = useT();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (seen()) return undefined;
    const timer = window.setTimeout(() => setOpen(true), 900);
    return () => window.clearTimeout(timer);
  }, []);

  function change(next) {
    if (!next) markSeen();
    setOpen(next);
  }

  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-primary/15 text-primary">
            <ApperIcon name="ShieldCheck" size={22} />
          </div>
          <DialogTitle>{t(C.dlgTitle)}</DialogTitle>
          <DialogDescription>
            {t(C.dlgDesc)}
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-4 text-sm">
          {POINTS.map((p) => (
            <li key={p.title} className="flex gap-3">
              <ApperIcon name={p.icon} size={16} className="mt-0.5 shrink-0 text-primary" />
              <div className="min-w-0">
                <p className="font-medium text-foreground">{t(p.title)}</p>
                <p className="text-muted-foreground">{t(p.text, { methods: t(METHODS) })}</p>
              </div>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button className="w-full sm:w-auto" onClick={() => change(false)}>
            {t(C.gotIt)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Short notice for Checkout, next to the order button. */
export function InlinePaymentNotice({ className = '' }) {
  const { t } = useT();
  return (
    <div className={`flex gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm ${className}`}>
      <ApperIcon name="ShieldCheck" size={18} className="mt-0.5 shrink-0 text-primary" />
      <p className="text-muted-foreground">
        <span className="font-medium text-foreground">{t(C.inlineBold)}</span>{' '}
        {t(C.inlineText, { methods: t(METHODS) })}
      </p>
    </div>
  );
}
